// 마감 초안 [초안 만들기] 판정 (2026-10-23 1단계 명세 §3-3·§3-4)
// 화면 버튼 한 번 방식 — 여기서는 "만들지 / 이어쓸지 / 막을지"만 정한다. 실제 담기·임시저장은 작성 화면이 [담기]와 같은 경로로 한다.
// 거래처당 작성 중 1개 정책은 바꾸지 않는다. console 에는 id·건수만.
import { collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { partnerKeyOfStatement, isMemberPartnerKey, kstYmd, lastClosedPeriod, getLabBilling } from '../../utils/billingCycle';

import { OUTSOURCE_KINDS } from '../../utils/receivables';   // 외주 종류 정본 (dev A)
const isLiveIssued = (s) => ['confirmed', 'published'].includes(s.status) && s.deletedBySender !== true;
const monthOfYmd = (ymd) => (ymd ? Number(String(ymd).slice(5, 7)) : null);

/**
 * 마감 판정에 쓸 이 기공소의 명세서 — 최근 기간 종료일 기준 발행본 + 작성 중 전부.
 * (where 필터 필수 · 인덱스 fromUserId+endDate DESC 는 목록 화면이 이미 쓰는 것)
 */
export async function loadStatementsForClosing(labId, now = new Date()) {
    const col = collection(db, 'transactionStatements');
    const lo = new Date(now.getTime() - 130 * 24 * 3600 * 1000);
    const [endSnap, draftSnap] = await Promise.all([
        getDocs(query(col, where('fromUserId', '==', labId), where('endDate', '>=', lo), orderBy('endDate', 'desc'))),
        getDocs(query(col, where('fromUserId', '==', labId), where('status', '==', 'draft'))),
    ]);
    const map = new Map();
    [...endSnap.docs, ...draftSnap.docs].forEach(d => { if (!map.has(d.id)) map.set(d.id, { id: d.id, ...d.data() }); });
    return Array.from(map.values()).filter(s => !OUTSOURCE_KINDS.includes(s.kind));
}

/**
 * 한 거래처의 마감 초안 상태.
 * @returns {{ state: 'issued'|'sameDraft'|'otherDraft'|'correction'|'none', period, statementId?, otherLabel?, clinicId }}
 *   issued      = 그 기간과 겹치는 살아 있는 발행본 있음 → "발행됨 ✓"로 접음
 *   sameDraft   = 같은 마감 기간 draft → 이어쓰기(새 수집분만 [담기] 병합, 확인창 없음)
 *   otherDraft  = 다른 기간 일반 draft → 만들지 않음, [그 명세서 열기]
 *   correction  = 정정 draft(correctsId) → 만들지 않음, [열기]
 *   none        = 새 draft 생성
 */
export function classifyClosing(partnerKey, closingDay, statements, now = new Date()) {
    const period = lastClosedPeriod(closingDay, now);
    if (!period) return null;
    const mine = statements.filter(s => partnerKeyOfStatement(s) === partnerKey);
    // 작성 화면에 넘길 거래처 id(toUserId) — 최근 문서의 것, 없으면 회원 키 그대로(비회원은 작성 화면이 이름으로 찾음)
    const withId = mine.find(s => s.toUserId);
    const clinicId = withId ? withId.toUserId : (isMemberPartnerKey(partnerKey) ? partnerKey : '');

    const issued = mine.find(s => isLiveIssued(s)
        && kstYmd(s.startDate || s.endDate) <= period.to && kstYmd(s.endDate || s.startDate) >= period.from);
    if (issued) return { state: 'issued', period, statementId: issued.id, clinicId };

    const drafts = mine.filter(s => s.status === 'draft');
    const correction = drafts.find(d => d.correctsId);
    if (correction) return { state: 'correction', period, statementId: correction.id, clinicId };
    const same = drafts.find(d => (d.closingDraft && d.closingDraft.from === period.from && d.closingDraft.to === period.to)
        || (kstYmd(d.startDate) === period.from && kstYmd(d.endDate) === period.to));
    if (same) return { state: 'sameDraft', period, statementId: same.id, clinicId };
    if (drafts.length > 0) {
        const d = drafts[0];
        const m = monthOfYmd(kstYmd(d.endDate) || kstYmd(d.startDate));
        return { state: 'otherDraft', period, statementId: d.id, otherLabel: m ? `${m}월분` : '이전 기간', clinicId };
    }
    return { state: 'none', period, clinicId };
}

// 막힌 경우 안내 문구 (명세 §3-4 그대로)
export const closingBlockedText = (c) => (c?.state === 'correction'
    ? '고쳐서 다시 발행 중인 명세서가 있습니다'
    : `${c?.otherLabel || '이전 기간'} 작성 중 명세서가 아직 안 보내졌습니다`);

// 작성 화면에 넘길 자동 초안 지시 (CreateTransactionStatement autoDraft prop)
export const closingAutoDraft = (partnerKey, partnerName, c) => ({
    mode: 'closing',
    partnerKey,
    partnerName: partnerName || '',
    from: c.period.from,
    to: c.period.to,
    label: c.period.label,
    closeDay: c.period.closingDay,
});

/**
 * 거래처 하나에 대해 [초안 만들기]를 판정 (수집함 [이 치과 초안 만들기] 진입용).
 * @returns classifyClosing 결과 | { state: 'noClosingDay', clinicId }
 */
export async function planClosingFor(labId, partnerKey, now = new Date()) {
    const billing = await getLabBilling(labId);
    const cd = billing?.partners?.[partnerKey]?.closingDay;
    const statements = await loadStatementsForClosing(labId, now);
    if (!cd) {
        const withId = statements.find(s => partnerKeyOfStatement(s) === partnerKey && s.toUserId);
        return { state: 'noClosingDay', clinicId: withId ? withId.toUserId : (isMemberPartnerKey(partnerKey) ? partnerKey : '') };
    }
    return classifyClosing(partnerKey, cd, statements, now);
}
